import { FileCheck, TrendingUp, Shield, ShieldCheck, ArrowRight, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';

export default function FrameworkPage() {
  const navigate = useNavigate();
  const { enabledFrameworks } = useContext(AuthContext);
  const [stats, setStats] = useState({
    iso27001: { total: 0, compliant: 0, nonCompliant: 0, partial: 0 },
    soc2: { total: 0, compliant: 0, nonCompliant: 0, partial: 0 },
    essential8: { total: 0, compliant: 0, nonCompliant: 0, partial: 0 },
    pcidss: { total: 0, compliant: 0, nonCompliant: 0, partial: 0 },
    hipaa: { total: 0, compliant: 0, nonCompliant: 0, partial: 0 },
    iso42001: { total: 0, compliant: 0, nonCompliant: 0, partial: 0 },
    recommendations: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [iso27001Response, soc2Response, essential8Response, pcidssResponse, hipaaResponse, iso42001Response] = await Promise.all([
        axios.get(`${API}/controls?framework=ISO27001`),
        axios.get(`${API}/controls?framework=SOC2`),
        axios.get(`${API}/controls?framework=Essential8`),
        axios.get(`${API}/controls?framework=PCIDSS`),
        axios.get(`${API}/controls?framework=HIPAA`),
        axios.get(`${API}/controls?framework=ISO42001`)
      ]);

      const iso27001Controls = iso27001Response.data;
      const soc2Controls = soc2Response.data;
      const essential8Controls = essential8Response.data;
      const pcidssControls = pcidssResponse.data;
      const hipaaControls = hipaaResponse.data;
      const iso42001Controls = iso42001Response.data;

      const iso27001Stats = {
        total: iso27001Controls.length,
        compliant: iso27001Controls.filter(c => c.status === 'Yes').length,
        nonCompliant: iso27001Controls.filter(c => c.status === 'No').length,
        partial: iso27001Controls.filter(c => c.status === 'Partially').length
      };

      const soc2Stats = {
        total: soc2Controls.length,
        compliant: soc2Controls.filter(c => c.status === 'Yes').length,
        nonCompliant: soc2Controls.filter(c => c.status === 'No').length,
        partial: soc2Controls.filter(c => c.status === 'Partially').length
      };

      const essential8Stats = {
        total: essential8Controls.length,
        compliant: essential8Controls.filter(c => c.status === 'Yes').length,
        nonCompliant: essential8Controls.filter(c => c.status === 'No').length,
        partial: essential8Controls.filter(c => c.status === 'Partially').length
      };

      const pcidssStats = {
        total: pcidssControls.length,
        compliant: pcidssControls.filter(c => c.status === 'Yes').length,
        nonCompliant: pcidssControls.filter(c => c.status === 'No').length,
        partial: pcidssControls.filter(c => c.status === 'Partially').length
      };

      const hipaaStats = {
        total: hipaaControls.length,
        compliant: hipaaControls.filter(c => c.status === 'Yes').length,
        nonCompliant: hipaaControls.filter(c => c.status === 'No').length,
        partial: hipaaControls.filter(c => c.status === 'Partially').length
      };

      const iso42001Stats = {
        total: iso42001Controls.length,
        compliant: iso42001Controls.filter(c => c.status === 'Yes').length,
        nonCompliant: iso42001Controls.filter(c => c.status === 'No').length,
        partial: iso42001Controls.filter(c => c.status === 'Partially').length
      };

      const totalRecommendations = [
        ...iso27001Controls.filter(c => c.status === 'No' || c.status === 'Partially' || !c.status),
        ...soc2Controls.filter(c => c.status === 'No' || c.status === 'Partially' || !c.status),
        ...essential8Controls.filter(c => c.status === 'No' || c.status === 'Partially' || !c.status),
        ...pcidssControls.filter(c => c.status === 'No' || c.status === 'Partially' || !c.status),
        ...hipaaControls.filter(c => c.status === 'No' || c.status === 'Partially' || !c.status),
        ...iso42001Controls.filter(c => c.status === 'No' || c.status === 'Partially' || !c.status)
      ].length;

      setStats({
        iso27001: iso27001Stats,
        soc2: soc2Stats,
        essential8: essential8Stats,
        pcidss: pcidssStats,
        hipaa: hipaaStats,
        iso42001: iso42001Stats,
        recommendations: totalRecommendations
      });
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateComplianceRate = (compliant, total) => {
    if (total === 0) return 0;
    return Math.round((compliant / total) * 100);
  };

  const frameworkOptions = [
    {
      id: 'iso27001',
      title: 'ISO 27001:2022',
      description: 'Information Security Management System - International standard for managing information security',
      icon: Shield,
      color: 'blue',
      bgColor: 'bg-slate-50',
      borderColor: 'border-slate-700',
      iconColor: 'text-slate-600',
      hoverColor: 'hover:border-slate-400',
      path: '/iso27001',
      stats: loading ? null : {
        total: stats.iso27001.total,
        compliant: stats.iso27001.compliant,
        complianceRate: calculateComplianceRate(stats.iso27001.compliant, stats.iso27001.total)
      }
    },
    {
      id: 'soc2',
      title: 'SOC 2 Type II',
      description: 'Service Organization Control - Trust Services Criteria for security, availability, and confidentiality',
      icon: ShieldCheck,
      color: 'purple',
      bgColor: 'bg-slate-50',
      borderColor: 'border-slate-700',
      iconColor: 'text-slate-600',
      hoverColor: 'hover:border-slate-400',
      path: '/soc2',
      stats: loading ? null : {
        total: stats.soc2.total,
        compliant: stats.soc2.compliant,
        complianceRate: calculateComplianceRate(stats.soc2.compliant, stats.soc2.total)
      }
    },
    {
      id: 'essential8',
      title: 'Essential Eight (ACSC)',
      description: 'Australian cybersecurity mitigation framework focused on preventing ransomware and cyber threats. Consists of 8 core strategies, assessed across four maturity levels (ML0–ML3).',
      icon: Shield,
      color: 'green',
      bgColor: 'bg-slate-50',
      borderColor: 'border-slate-700',
      iconColor: 'text-green-600',
      hoverColor: 'hover:border-slate-400',
      path: '/essential8',
      stats: loading ? null : {
        total: stats.essential8.total,
        compliant: stats.essential8.compliant,
        complianceRate: calculateComplianceRate(stats.essential8.compliant, stats.essential8.total)
      }
    },
    {
      id: 'pcidss',
      title: 'PCI DSS',
      description: 'Global security standard for protecting payment card data. Comprises 12 security requirements grouped under 6 control objectives for securing cardholder information.',
      icon: ShieldCheck,
      color: 'orange',
      bgColor: 'bg-slate-50',
      borderColor: 'border-slate-700',
      iconColor: 'text-orange-600',
      hoverColor: 'hover:border-slate-400',
      path: '/pci-dss',
      stats: loading ? null : {
        total: stats.pcidss.total,
        compliant: stats.pcidss.compliant,
        complianceRate: calculateComplianceRate(stats.pcidss.compliant, stats.pcidss.total)
      }
    },
    {
      id: 'hipaa',
      title: 'HIPAA Security Rule',
      description: 'U.S. regulatory framework for protecting electronic Protected Health Information (ePHI). Defines administrative, physical, and technical safeguards for healthcare data security.',
      icon: Shield,
      color: 'teal',
      bgColor: 'bg-slate-50',
      borderColor: 'border-slate-700',
      iconColor: 'text-teal-600',
      hoverColor: 'hover:border-slate-400',
      path: '/hipaa',
      stats: loading ? null : {
        total: stats.hipaa.total,
        compliant: stats.hipaa.compliant,
        complianceRate: calculateComplianceRate(stats.hipaa.compliant, stats.hipaa.total)
      }
    },
    {
      id: 'iso42001',
      title: 'ISO/IEC 42001:2023',
      description: 'International standard for Artificial Intelligence Management Systems (AIMS). Establishes requirements for responsible, ethical, and secure AI governance, including risk and lifecycle management.',
      icon: ShieldCheck,
      color: 'violet',
      bgColor: 'bg-slate-50',
      borderColor: 'border-violet-700',
      iconColor: 'text-violet-600',
      hoverColor: 'hover:border-violet-400',
      path: '/iso42001',
      stats: loading ? null : {
        total: stats.iso42001.total,
        compliant: stats.iso42001.compliant,
        complianceRate: calculateComplianceRate(stats.iso42001.compliant, stats.iso42001.total)
      }
    }
  ];

  // Map internal IDs to API framework IDs
  const frameworkIdMap = {
    'iso27001': 'ISO27001',
    'soc2': 'SOC2',
    'essential8': 'Essential8',
    'pcidss': 'PCIDSS',
    'hipaa': 'HIPAA',
    'iso42001': 'ISO42001'
  };

  // Filter frameworks based on enabled list
  const filteredFrameworks = frameworkOptions.filter(fw => {
    const apiId = frameworkIdMap[fw.id];
    return enabledFrameworks?.includes(apiId) ?? true; // Show all if enabledFrameworks not loaded
  });

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3 mb-2">
          <FileCheck className="w-8 h-8 text-slate-700" />
          <h1 className="text-3xl font-heading font-bold">Compliance Frameworks</h1>
        </div>
        <p className="text-gray-700">
          Select a framework to view controls, track compliance, and manage remediation efforts
        </p>
      </div>

      {/* Framework Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredFrameworks.map((framework) => {
          const Icon = framework.icon;
          
          return (
            <div
              key={framework.id}
              onClick={() => navigate(framework.path)}
              className={`${framework.bgColor} border-2 ${framework.borderColor} ${framework.hoverColor} rounded-lg p-6 cursor-pointer transition-all hover:shadow-lg group`}
            >
              {/* Icon and Badge */}
              <div className="flex items-start justify-between mb-4">
                <div className={`p-3 ${framework.bgColor} border ${framework.borderColor} rounded-lg`}>
                  <Icon className={`w-8 h-8 ${framework.iconColor}`} />
                </div>
                {framework.stats?.showBadge && (
                  <span className="px-3 py-1 bg-slate-100 text-red-800 text-sm font-semibold rounded-full">
                    {framework.stats.count} Issues
                  </span>
                )}
              </div>

              {/* Title */}
              <h3 className="text-xl font-bold mb-2 text-gray-900 group-hover:text-gray-700">
                {framework.title}
              </h3>

              {/* Description */}
              <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                {framework.description}
              </p>

              {/* Stats Section */}
              {framework.stats && !framework.stats.showBadge && (
                <div className="space-y-2 mb-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Total Controls:</span>
                    <span className="font-semibold">{framework.stats.total}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Compliant:</span>
                    <span className="font-semibold text-green-600">{framework.stats.compliant}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Compliance Rate:</span>
                    <span className={`font-semibold ${
                      framework.stats.complianceRate >= 80 ? 'text-green-600' :
                      framework.stats.complianceRate >= 50 ? 'text-slate-600' :
                      'text-red-600'
                    }`}>
                      {framework.stats.complianceRate}%
                    </span>
                  </div>
                  
                  {/* Progress Bar */}
                  <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                    <div
                      className={`h-2 rounded-full ${
                        framework.stats.complianceRate >= 80 ? 'bg-slate-600' :
                        framework.stats.complianceRate >= 50 ? 'bg-slate-600' :
                        'bg-slate-600'
                      }`}
                      style={{ width: `${framework.stats.complianceRate}%` }}
                    />
                  </div>
                </div>
              )}

              {framework.stats?.showBadge && (
                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <AlertTriangle className="w-4 h-4 text-slate-600" />
                    <span>Requires immediate attention</span>
                  </div>
                </div>
              )}

              {/* Action Button */}
              <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                <span className={`text-sm font-medium ${framework.iconColor}`}>
                  View Details
                </span>
                <ArrowRight className={`w-5 h-5 ${framework.iconColor} group-hover:translate-x-1 transition-transform`} />
              </div>
            </div>
          );
        })}
      </div>

      {/* Overview Section */}
      <div className="bg-white border border-border rounded-sm p-6">
        <h2 className="text-xl font-bold mb-4">Framework Overview</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Total Controls */}
          <div className="text-center p-4 bg-slate-50 rounded-lg">
            <div className="text-3xl font-bold text-slate-700 mb-1">
              {stats.iso27001.total + stats.soc2.total + stats.essential8.total + stats.pcidss.total + stats.hipaa.total + stats.iso42001.total}
            </div>
            <div className="text-sm text-gray-600">Total Controls</div>
          </div>

          {/* Compliant Controls */}
          <div className="text-center p-4 bg-slate-50 rounded-lg">
            <div className="flex items-center justify-center gap-2 mb-1">
              <CheckCircle className="w-6 h-6 text-green-600" />
              <div className="text-3xl font-bold text-green-600">
                {stats.iso27001.compliant + stats.soc2.compliant + stats.essential8.compliant + stats.pcidss.compliant + stats.hipaa.compliant + stats.iso42001.compliant}
              </div>
            </div>
            <div className="text-sm text-gray-600">Compliant Controls</div>
          </div>

          {/* Non-Compliant Controls */}
          <div className="text-center p-4 bg-slate-50 rounded-lg">
            <div className="flex items-center justify-center gap-2 mb-1">
              <XCircle className="w-6 h-6 text-red-600" />
              <div className="text-3xl font-bold text-red-600">
                {stats.recommendations}
              </div>
            </div>
            <div className="text-sm text-gray-600">Needs Attention</div>
          </div>
        </div>

        {/* Framework Breakdown Table */}
        <div className="mt-6 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-2 px-3 font-semibold text-gray-700">Framework</th>
                <th className="text-center py-2 px-3 font-semibold text-gray-700">Total</th>
                <th className="text-center py-2 px-3 font-semibold text-gray-700">Compliant</th>
                <th className="text-center py-2 px-3 font-semibold text-gray-700">Non-Compliant</th>
                <th className="text-center py-2 px-3 font-semibold text-gray-700">Partially Compliant</th>
                <th className="text-center py-2 px-3 font-semibold text-gray-700">Compliance Rate</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-gray-100 hover:bg-gray-50">
                <td className="py-2 px-3 font-medium">ISO 27001</td>
                <td className="py-2 px-3 text-center">{stats.iso27001.total}</td>
                <td className="py-2 px-3 text-center text-green-600">{stats.iso27001.compliant}</td>
                <td className="py-2 px-3 text-center text-red-600">{stats.iso27001.nonCompliant}</td>
                <td className="py-2 px-3 text-center text-yellow-600">{stats.iso27001.partial}</td>
                <td className="py-2 px-3 text-center font-semibold">{calculateComplianceRate(stats.iso27001.compliant, stats.iso27001.total)}%</td>
              </tr>
              <tr className="border-b border-gray-100 hover:bg-gray-50">
                <td className="py-2 px-3 font-medium">SOC 2</td>
                <td className="py-2 px-3 text-center">{stats.soc2.total}</td>
                <td className="py-2 px-3 text-center text-green-600">{stats.soc2.compliant}</td>
                <td className="py-2 px-3 text-center text-red-600">{stats.soc2.nonCompliant}</td>
                <td className="py-2 px-3 text-center text-yellow-600">{stats.soc2.partial}</td>
                <td className="py-2 px-3 text-center font-semibold">{calculateComplianceRate(stats.soc2.compliant, stats.soc2.total)}%</td>
              </tr>
              <tr className="border-b border-gray-100 hover:bg-gray-50">
                <td className="py-2 px-3 font-medium">Essential 8</td>
                <td className="py-2 px-3 text-center">{stats.essential8.total}</td>
                <td className="py-2 px-3 text-center text-green-600">{stats.essential8.compliant}</td>
                <td className="py-2 px-3 text-center text-red-600">{stats.essential8.nonCompliant}</td>
                <td className="py-2 px-3 text-center text-yellow-600">{stats.essential8.partial}</td>
                <td className="py-2 px-3 text-center font-semibold">{calculateComplianceRate(stats.essential8.compliant, stats.essential8.total)}%</td>
              </tr>
              <tr className="border-b border-gray-100 hover:bg-gray-50">
                <td className="py-2 px-3 font-medium">PCI DSS</td>
                <td className="py-2 px-3 text-center">{stats.pcidss.total}</td>
                <td className="py-2 px-3 text-center text-green-600">{stats.pcidss.compliant}</td>
                <td className="py-2 px-3 text-center text-red-600">{stats.pcidss.nonCompliant}</td>
                <td className="py-2 px-3 text-center text-yellow-600">{stats.pcidss.partial}</td>
                <td className="py-2 px-3 text-center font-semibold">{calculateComplianceRate(stats.pcidss.compliant, stats.pcidss.total)}%</td>
              </tr>
              <tr className="border-b border-gray-100 hover:bg-gray-50">
                <td className="py-2 px-3 font-medium">HIPAA</td>
                <td className="py-2 px-3 text-center">{stats.hipaa.total}</td>
                <td className="py-2 px-3 text-center text-green-600">{stats.hipaa.compliant}</td>
                <td className="py-2 px-3 text-center text-red-600">{stats.hipaa.nonCompliant}</td>
                <td className="py-2 px-3 text-center text-yellow-600">{stats.hipaa.partial}</td>
                <td className="py-2 px-3 text-center font-semibold">{calculateComplianceRate(stats.hipaa.compliant, stats.hipaa.total)}%</td>
              </tr>
              <tr className="border-b border-gray-100 hover:bg-gray-50">
                <td className="py-2 px-3 font-medium">ISO 42001</td>
                <td className="py-2 px-3 text-center">{stats.iso42001.total}</td>
                <td className="py-2 px-3 text-center text-green-600">{stats.iso42001.compliant}</td>
                <td className="py-2 px-3 text-center text-red-600">{stats.iso42001.nonCompliant}</td>
                <td className="py-2 px-3 text-center text-yellow-600">{stats.iso42001.partial}</td>
                <td className="py-2 px-3 text-center font-semibold">{calculateComplianceRate(stats.iso42001.compliant, stats.iso42001.total)}%</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-slate-50 border border-slate-700 rounded-sm p-4">
        <div className="flex items-start gap-3">
          <FileCheck className="w-5 h-5 text-slate-600 mt-0.5" />
          <div>
            <h3 className="font-semibold text-slate-900 mb-1">Getting Started</h3>
            <p className="text-sm text-slate-800">
              Select a framework to review controls and manage compliance.
              Each framework page allows you to update control status, assign owners, set due dates, and track remediation progress.
              Filter by non-compliant controls to focus on priority items requiring immediate attention.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
